import React, {useCallback, useEffect, useRef} from 'react';
import {createPortal} from 'react-dom';
import {ArrowUpIcon, Squares2X2Icon, TrashIcon} from '@heroicons/react/24/outline';
import {useNode, useEditor} from '@craftjs/core';
import {ROOT_NODE} from '@craftjs/utils';
import {classNames, suffixRemove} from 'utils';

import iconLookUp from '../layers/iconLookup';

interface IEditorContentParentProps {
  render?: React.ReactElement;
}

const EditorContentParent = ({render}: IEditorContentParentProps) => {
  const {id} = useNode();
  const {actions, query, isSelected} = useEditor((_, query) => ({
    isSelected: query.getEvent('selected').contains(id),
  }));

  const {
    isHover,
    dom,
    name,
    icon,
    moveable,
    deletable,
    connectors: {drag},
    parent,
  } = useNode(node => ({
    isHover: node.events.hovered,
    dom: node.dom,
    name: node.data.custom.displayName || node.data.displayName,
    icon: node.data.custom.icon as string,
    moveable: query.node(node.id).isDraggable(),
    deletable: query.node(node.id).isDeletable(),
    parent: node.data.parent,
    props: node.data.props,
  }));

  const currentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dom) {
      if (isSelected || isHover) dom.classList.add('component-selected');
      else dom.classList.remove('component-selected');
    }
  }, [dom, isSelected, isHover]);

  const getPos = React.useCallback((dom: HTMLElement) => {
    const {top, left, bottom} = dom ? dom.getBoundingClientRect() : {top: 0, left: 0, bottom: 0};
    return {
      top: `${top > 0 ? top : bottom}px`,
      left: `${left}px`,
    };
  }, []);

  const scroll = useCallback(() => {
    const {current: currentDOM} = currentRef;
    if (!currentDOM || !dom) {
      return;
    }
    const {top, left} = getPos(dom);
    currentDOM.style.top = top;
    currentDOM.style.left = left;
  }, [dom, getPos]);

  useEffect(() => {
    document.querySelector('.craftjs-renderer')?.addEventListener('scroll', scroll);
    return () => {
      document.querySelector('.craftjs-renderer')?.removeEventListener('scroll', scroll);
    };
  }, [scroll]);

  const pageContainer = document.querySelector('.page-container');

  return (
    <>
      {isSelected && !!dom && !!pageContainer
        ? createPortal(
            <div
              ref={currentRef}
              className="z-20 p-1 text-xs text-white font-medium bg-primary fixed flex items-center"
              style={{
                left: getPos(dom).left,
                top: `${suffixRemove('px', getPos(dom).top) - 24}px`,
              }}>
              {(() => {
                const Icon = iconLookUp[icon];
                return Icon ? <Icon className="size-4 mr-1" /> : null;
              })()}
              <h2>{name}</h2>
              <div className={classNames('flex items-center gap-2', moveable && 'ml-2')}>
                {moveable ? (
                  <div
                    ref={node => {
                      node && drag(node);
                    }}>
                    <Squares2X2Icon className="size-4 cursor-move" />
                  </div>
                ) : null}
                {id !== ROOT_NODE && !!parent && <ArrowUpIcon className="size-4 cursor-pointer" onClick={() => actions.selectNode(parent)} />}
                {deletable && (
                  <TrashIcon
                    className="size-4 cursor-pointer"
                    onClick={() => {
                      actions.delete(id);
                    }}
                  />
                )}
              </div>
            </div>,
            pageContainer,
          )
        : null}
      {render}
    </>
  );
};

export default React.memo(EditorContentParent);
